import { useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';

import { HotelFareFinalDto, PaxResponseDto } from '@bandido/shared/dtos';
import { fromDDMMYYYYToDate, fromDDMMYYYYToYYYYMMDD, fromYYYYMMDDToDDMMYYYY } from '@frontend/features/common/helper';

import { Form, CartPaxForm } from './form';
import { CartPaxes } from './cartPaxes';
import { useUpdateSaleOrderItemOccupancies } from '../../queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@frontend/features/common/types';

type Props = Pick<HotelFareFinalDto, 'occupancies'>  & {
  saleOrderItemId: string;
  saleOrderId: string;
  onBack: () => void;
  onContinue: () => void;
  cartPaxes?: PaxResponseDto[];
  checkinDate?: string;
}

const useValidationSchema = () => {
  const { t } = useTranslation();

  return yup.object().shape({
    occupancies: yup.array().of(
      yup.object().shape({
        id: yup.number().required(),
        paxes: yup.array().of(
          yup.object().shape({
            id: yup.string().required(),
            age: yup.number().required(),
            ageOnSearch: yup.number().required(),
            name: yup.string().required(t('hotels.fares.hotelPaxData.name.required')),
            surname: yup.string().required(t('hotels.fares.hotelPaxData.surname.required')),
            dob: yup.string()
              .required(t('hotels.fares.hotelPaxData.birthDate.required'))
              .test(
                'is-valid-date',
                t('hotels.fares.hotelPaxData.birthDate.notValid'),
                (value) => !!value && fromYYYYMMDDToDDMMYYYY(value) !== null
              )
              .test(
                'is-past-date',
                t('hotels.fares.hotelPaxData.birthDate.notPast'),
                (value) => {
                  const date = fromDDMMYYYYToDate(value || '');
                  return !!date && date <= new Date();
                }
              ),
          })
        ).required(),
      })
    ).required(),
  });
};

export const HotelPaxSelector = ({
  saleOrderItemId,
  saleOrderId,
  onBack,
  onContinue,
  occupancies,
  cartPaxes,
  checkinDate,
}: Props) => {
  const formRef = useRef<HTMLFormElement>(null);
  const validationSchema = useValidationSchema();
  const { t } = useTranslation();
  const {
    control,
    formState,
    handleSubmit,
    register,
    setValue,
    getValues,
    reset
  } = useForm<Form>({
    defaultValues: { occupancies: [] },
    resolver: yupResolver(validationSchema),
  });
  const { mutate: updateItem, isLoading: isUpdating } = useUpdateSaleOrderItemOccupancies();
  const { fields } = useFieldArray({
    control,
    name: 'occupancies',
  });
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();

  const onSubmit = (data: Form) => {
    const parsedData = data.occupancies.map(({ id, paxes }) => ({
      id,
      paxes: paxes.map((pax) => ({
        id: pax.id,
        name: pax.name,
        age: pax.age,
        ageOnSearch: pax.ageOnSearch,
        surname: pax.surname,
        dob: fromDDMMYYYYToYYYYMMDD(pax.dob),
      }))
    }));
    updateItem({
      itemId: saleOrderItemId,
      saleOrderId: saleOrderId,
      occupancies: parsedData,
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersDetails, saleOrderId] });
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersPaxes, saleOrderId] });
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersItems, saleOrderId] });
        onContinue();
      },
      onError: () => {
        addToast({
          message: t('saleOrder.view.passengers.errorUpdatingPax'),
          color: 'error',
        });
      },
    });
  };

  useEffect(() => {
    if (occupancies?.length && getValues('occupancies')) {
      reset({
        occupancies: occupancies?.map(({ id, paxes }) => ({
          id,
          paxes: paxes.map(({ age, ageOnSearch }: { age: number, ageOnSearch: number }) => ({ 
            id: uuid(), 
            name: '', 
            surname: '', 
            dob: '',
            ageOnSearch: ageOnSearch || age,
            age,
          }))
        }))
      });
    }
  }, [occupancies]);

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <Dialog open>
        <DialogTitle>{t('hotels.fares.hotelPaxData.pax.addInfo')}</DialogTitle>
        <DialogContent>
          {fields.length === 1 ? (
            <Box display="flex" flexDirection="column" gap={3}> 
              <Typography variant="subtitle2">
                {t('hotels.fares.room.title')} {1}
              </Typography>
              {fields[0].paxes.map(({ ageOnSearch }, paxIndex) => (
                <Box key={`occupancy_pax_${paxIndex}`} display="flex" flexDirection="column" gap={2} minWidth="400px">
                    <Typography
                      variant='body2'
                      color="text.primary"
                    >
                      {`${t('hotels.fares.hotelPaxData.form.index', { count: paxIndex + 1 })} (${t('hotels.fares.hotelPaxData.form.age', { count: ageOnSearch })})`}

                    </Typography>
                    {Boolean(cartPaxes?.length) &&
                      <CartPaxes
                        setValue={setValue}
                        cartPaxes={cartPaxes || []}
                        occupancyIndex={0}
                        paxIndex={paxIndex}
                        checkinDate={checkinDate}
                      />
                    }
                    <CartPaxForm
                      register={register}
                      setValue={setValue}
                      occupancyIndex={0}
                      paxIndex={paxIndex}
                      control={control}
                      checkinDate={checkinDate}
                    />
                </Box>
              ))}
            </Box>
          ) : (
            fields.map((occupancy, occupancyIndex) => (
              <Box
                key={`occupancy_${occupancyIndex}`}
                display="flex"
                flexDirection="column"
                minWidth="400px"
                gap={2}
                my={2}
              >
                <Accordion defaultExpanded={occupancyIndex === 0}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>{t('hotels.fares.room.title')} {occupancyIndex + 1}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {
                      occupancy.paxes.map(
                        ({ ageOnSearch }, paxIndex) => (
                          <Box
                            display="flex"
                            flexDirection="column"
                            key={`occupancy_pax_${paxIndex}`}
                            gap={2}
                            my={2}
                          >
                            <Typography
                              variant='body2'
                              color="text.primary"
                            >
                              {`${t('hotels.fares.hotelPaxData.form.index', { count: paxIndex + 1 })} (${t('hotels.fares.hotelPaxData.form.age', { count: ageOnSearch })})`}
                            </Typography>
                            {Boolean(cartPaxes?.length) &&
                              <CartPaxes
                                setValue={setValue}
                                cartPaxes={cartPaxes || []}
                                occupancyIndex={occupancyIndex}
                                paxIndex={paxIndex}
                                checkinDate={checkinDate}
                              />
                            }
                            <CartPaxForm
                              register={register}
                              setValue={setValue}
                              occupancyIndex={occupancyIndex}
                              paxIndex={paxIndex}
                              control={control}
                              checkinDate={checkinDate}
                            />
                          </Box>
                        )
                      )
                    }
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onBack}
            variant="outlined"
          >
            {t('common.cancel')}
          </Button>
          <LoadingButton
            loading={isUpdating}
            variant="contained"
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={!formState.isValid}
          >
            {t('common.continue')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </form>
  );
};
